import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "@headlessui/react";
import { Button, Dropdown, DropdownButton, DropdownBody } from "@components";
import { LayoutThreeColumns } from "react-bootstrap-icons";
import { moveColumn } from "./columnOrder";

const ColumnRowBody = ({ id, title, hide, onToggleHide }) => (
  <div className="flex items-center grow py-2 pl-4">
    <Checkbox
      checked={!hide}
      onChange={() => onToggleHide(id)}
      className="mr-3 group block size-3.5 rounded border border-zinc-300 bg-white  data-[checked]:border-primary data-[checked]:bg-primary cursor-pointer"
    >
      <svg
        className="stroke-white opacity-0 group-data-[checked]:opacity-100"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path
          d="M3 8L6 11L11 3.5"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Checkbox>
    <p className="text-zinc-600 mr-auto text-sm">{title}</p>
  </div>
);

// Keeps a handle-less row the same width as the ones that have a handle.
const DragHandleSpacer = () => (
  <span className="px-3 py-2 invisible select-none" aria-hidden="true">
    ⋮⋮⋮
  </span>
);

// A filter-only column is never drawn in the table, so it has no position. It is rendered
// outside the drag context entirely rather than merely losing its handle: an item inside the
// sortable context stays a drop target, so other columns could still be dragged onto it.
const StaticColumnItem = ({ user, onToggleHide }) => (
  <div className="flex justify-start items-center">
    <ColumnRowBody
      id={user.id}
      title={user.title}
      hide={user.hide}
      onToggleHide={onToggleHide}
    />
    <DragHandleSpacer />
  </div>
);

const ColumnItem = ({ user, onToggleHide }) => {
  const { id, title, hide } = user;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cursorStyle = isDragging ? "cursor-grabbing" : "cursor-grab";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex justify-start items-center"
    >
      <ColumnRowBody id={id} title={title} hide={hide} onToggleHide={onToggleHide} />
      <button
        {...attributes}
        {...listeners}
        className={`px-3 py-2 ${cursorStyle}`}
      >
        ⋮⋮⋮
      </button>
    </div>
  );
};

export const ManageColumnsList = ({ setData, data, onReset, hideHeader = false }) => {
  const fixedItems = data.filter((item) => item.filterOnly);
  const sortableItems = data.filter((item) => !item.filterOnly);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleToggleHide(itemId) {
    setData((items) =>
      items.map((item) =>
        item.id === itemId ? { ...item, hide: !item.hide } : item,
      ),
    );
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setData((items) => moveColumn(items, active.id, over.id));
  };

  return (
    <div className="max-w-2xl mx-auto grid w-full">
      {!hideHeader && (
        <span className="text-md font-medium px-4 py-3 border-b">
          Manage columns
        </span>
      )}
      <div className="py-1 max-h-96 overflow-auto">
        {fixedItems.map((item) => (
          <StaticColumnItem
            key={item.id}
            user={item}
            onToggleHide={handleToggleHide}
          />
        ))}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {sortableItems.map((item) => (
              <ColumnItem
                key={item.id}
                user={item}
                onToggleHide={handleToggleHide}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <div className="py-2 pl-2 border-t">
        <Button onClick={onReset} color="secondaryLink">
          Reset
        </Button>
      </div>
    </div>
  );
};

const ManageColumnsButton = ({ onReset, setData, data }) => {
  return (
    <Dropdown>
      <DropdownButton>
        <Button color="secondary">
          <LayoutThreeColumns className="" />
          <span className="ms-2">Columns</span>
        </Button>
      </DropdownButton>

      <DropdownBody>
        <ManageColumnsList onReset={onReset} setData={setData} data={data} />
      </DropdownBody>
    </Dropdown>
  );
};

export default ManageColumnsButton;
